import {Alert, Anchor, List, Paper, Stack, Table, Text} from '@mantine/core'
import {useMemo} from 'react'

import {
    crossYardDoubleBookings,
    doubleBookingMessage,
    type LentJob,
    lentJobsForYard,
} from '../dispatch/cross-yard'
import type {DispatchYard} from '../dispatch/schemas'
import type {DispatchTimelineData} from './types'

type CrossYardPanelProps = {
    data: DispatchTimelineData
    yardId: string | null
    yards: DispatchYard[]
    onOpenJob: (jobId: string) => void
}

/**
 * Shows drivers double-booked across yards, and the other yards' jobs this yard's drivers
 * are lent to. Lent jobs open read-only unless the user can already dispatch them.
 */
export function CrossYardPanel({data, yardId, yards, onOpenJob}: CrossYardPanelProps) {
    const doubleBookings = useMemo(
        () => crossYardDoubleBookings(data, yardId, yards),
        [data, yardId, yards],
    )
    const lentByYard = useMemo(() => {
        const groups = new Map<string, LentJob[]>()
        for (const job of yardId == null ? [] : lentJobsForYard(data, yardId, yards)) {
            groups.set(job.yardName, [...(groups.get(job.yardName) ?? []), job])
        }
        return [...groups.entries()]
    }, [data, yardId, yards])

    if (doubleBookings.length === 0 && lentByYard.length === 0) {
        return null
    }

    return (
        <Stack gap="sm" mb="sm">
            {doubleBookings.length > 0 ? (
                <Alert
                    color="orange"
                    variant="light"
                    title="Booked with two yards on the same day"
                    data-testid="cross-yard-double-bookings"
                >
                    <List size="sm" spacing={2}>
                        {doubleBookings.map((booking) => (
                            <List.Item key={`${booking.driverId}-${booking.day}`}>
                                {doubleBookingMessage(booking)}
                            </List.Item>
                        ))}
                    </List>
                    <Text size="xs" c="dimmed" mt={4}>
                        Check with the other yard&apos;s dispatcher. Nothing is blocked.
                    </Text>
                </Alert>
            ) : null}
            {lentByYard.map(([yardName, jobs]) => (
                <Paper
                    key={yardName}
                    withBorder
                    radius="md"
                    p="sm"
                    data-testid="cross-yard-lent-jobs"
                >
                    <Text fw={700}>
                        Our drivers on {yardName} jobs · {jobs.length}
                    </Text>
                    <Text size="xs" c="dimmed" mb="xs">
                        {yardName} dispatches these jobs. Shown so you know where your drivers are.
                    </Text>
                    <Table.ScrollContainer minWidth={640}>
                        <Table striped withRowBorders={false} verticalSpacing={4} fz="sm">
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>When</Table.Th>
                                    <Table.Th>Job</Table.Th>
                                    <Table.Th>Job #</Table.Th>
                                    <Table.Th>Rep</Table.Th>
                                    <Table.Th>Customer · where</Table.Th>
                                    <Table.Th>Our drivers</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {jobs.map((job) => (
                                    <Table.Tr key={job.jobId}>
                                        <Table.Td style={{whiteSpace: 'nowrap'}}>
                                            {job.when}
                                        </Table.Td>
                                        <Table.Td>
                                            <Anchor
                                                component="button"
                                                size="sm"
                                                onClick={() => onOpenJob(job.jobId)}
                                            >
                                                {job.jobName}
                                            </Anchor>
                                        </Table.Td>
                                        <Table.Td>{job.displayId}</Table.Td>
                                        <Table.Td>{job.repName}</Table.Td>
                                        <Table.Td>
                                            {[job.customer, job.where].filter(Boolean).join(' · ')}
                                        </Table.Td>
                                        <Table.Td>{job.drivers.join(', ')}</Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                </Paper>
            ))}
        </Stack>
    )
}
